<template>
    <div class="product-selector-wrapper">
        <div class="row justify-content-end align-items-center mb-3">
            <div class="col">
                <slot name="header-actions" />
            </div>
            <b-form inline class="col-auto fixed-content" @submit.prevent>
                <b-pagination
                    v-model="currentPage"
                    size="md"
                    class="mb-0"
                    :total-rows="totalCount"
                    :per-page="productsPerPage"
                />
                <b-form-input class="ml-3" placeholder="Search" debounce="400" @update="updateSearchString" />
            </b-form>
        </div>

        <div class="list-group scrollable-content mb-3">
            <div
                v-for="product in filteredProducts"
                :key="`product-${product.id}`"
                class="list-group-item product-item d-flex flex-wrap align-items-center justify-content-between"
                @click="onProductSelect(product)"
            >
                <div class="d-flex align-items-center">
                    <div v-if="product.image" class="product-img">
                        <b-img rounded thumbnail v-bind="thumbsProps" :src="product.image" />
                    </div>
                    <strong class="ml-3">{{ product.title }}</strong>
                </div>
                <div v-if="selectedProducts.some((p) => p.id === product.id)" class="d-flex align-items-center">
                    <b-btn variant="danger" @click.stop="onUnassign(product)">Unassign</b-btn>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { Ref, computed, ref, watchEffect } from 'vue';
import { GenericPodProduct } from './typings';

const emit = defineEmits(['input', 'product-select', 'product-unassign']);

const props = withDefaults(
    defineProps<{
        products?: GenericPodProduct[];
        value?: GenericPodProduct[];
    }>(),
    {
        products: () => [],
        value: () => []
    }
);

const thumbsProps = {
    width: 50,
    height: 50
};
let internalSelectedProducts: GenericPodProduct[] = [];
const productsPerPage = 10;
const searchString = ref('');
const currentPage = ref(1);
const totalCount = ref(0);
const filteredProducts: Ref<GenericPodProduct[]> = ref([]);

const selectedProducts = computed({
    get(): GenericPodProduct[] {
        return props.value;
    },
    set(value: GenericPodProduct[]) {
        internalSelectedProducts = value;
        emit('input', value);
    }
});

watchEffect(() => {
    if (!props.products) return [];

    let filtered = props.products;

    if (searchString.value.length) {
        const searchValue = searchString.value.trim().toLowerCase();
        // Filter by search string
        filtered = filtered.filter(
            (p) =>
                p.title.toLowerCase().includes(searchValue) ||
                (p.description && p.description.toLowerCase().includes(searchValue))
        );
    }

    totalCount.value = filtered.length;
    // Filter by pagination
    const startIndex = (currentPage.value - 1) * productsPerPage;
    const endIndex = startIndex + productsPerPage;
    filteredProducts.value = filtered.slice(startIndex, endIndex);
});

const updateSearchString = (value: string) => {
    searchString.value = value;
};

const onProductSelect = (product: GenericPodProduct) => {
    if (!internalSelectedProducts.some((p) => p.id === product.id)) {
        internalSelectedProducts.push(product);
    }
    emit('input', internalSelectedProducts);
    emit('product-select', product);
};

const onUnassign = (product: GenericPodProduct) => {
    const index = internalSelectedProducts.findIndex((p) => p.id === product.id);
    if (index > -1) {
        internalSelectedProducts.splice(index, 1);
    }
    emit('input', internalSelectedProducts);
    emit('product-unassign', product);
};
</script>

<style lang="scss" scoped>
.product-selector-wrapper {
    .product-item {
        cursor: pointer;
    }
    .product-img {
        width: 50px;
        height: 50px;
        img {
            max-height: 100%;
        }
    }
}
</style>
